/* eslint-disable max-lines */
import { useCombobox, UseComboboxStateChange } from "downshift";
import { isEqual } from "lodash";
import { useMemo, useState } from "react";
import { cx } from "../../../utils/classnames";
import {
  buildLegSearchScope,
  legSearchApiTerm
} from "../../../utils/searchText";
import useSelect from "./hook/useSelect";
import { ISelectAsync, ISelectOptions } from "./Select.types";
import List from "./shared/List";
import SelectInput from "./shared/SelectInput";
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const Select = <T, D extends unknown>({
  asyncFunc,
  getOptionLabel,
  itemToString,
  selectedItem: _selectedItem,
  selectedItems,
  inputClassName,
  emptyItem,
  onSelectedItemChange,
  className,
  pageSize,
  options,
  searchQuerySelector,
  searchFunc,
  selectType = "single",
  onSelectedListChange,
  dataSelector,
  extraParams,
  maxHeightList,
  isAbsolute,
  getOptionValue: _getOptionValue,
  defaultIsOpen,
  enableKeyboardNav,
  onEnterSelect,
  inlineSearch,
  prioritizePrefixMatches,
  onSelectAll,
  selectAllLabel,
  onUnselectAll,
  unselectAllLabel,
  ...props
}: ISelectAsync<T, D> | ISelectOptions<T, D>) => {
  // Opt-in: the trigger input IS the search box, so the in-menu search input is
  // never rendered and the pill overlay is skipped. Multi-select only.
  const inlineMultiSearch = !!inlineSearch && selectType === "multiple";
  // Only route keys to the in-menu search input for multi-select opt-ins.
  // Single-select already gets full nav from the outer input; leaving it off
  // there avoids fighting Downshift's single input ref. Never with
  // inlineSearch — there is no in-menu input to route to, and the outer input
  // must keep getInputProps.
  const keyboardNav =
    !inlineMultiSearch && !!enableKeyboardNav && selectType === "multiple";
  // Opt-in: when a consumer wants Enter to advance focus, Enter closes the menu
  // and fires onEnterSelect. Gated to the keyboard-nav multi-select so no other
  // consumer's keep-open-on-Enter behavior changes.
  const closeOnEnter = keyboardNav && !!onEnterSelect;
  const getOptionValue = (value?: T|null) => _getOptionValue ? _getOptionValue(value) : value;
  const {
    items: fetchedItems,
    onInputValueChange,
    loading,
    handleScroll,
    itemToString: _itemToString
  } = useSelect({
    emptyItem, pageSize, dataSelector, searchQuerySelector, itemToString, disabled: props.disabled,
    // Strip the leg separator out of the request: the API can't filter on it,
    // so it fetches on the city alone and the direction is applied below. A
    // module-level function on purpose — _handleFunc doesn't list this in its
    // deps, so an inline lambda would go stale.
    ...(prioritizePrefixMatches ? { searchTermTransform: legSearchApiTerm } : {}),
    ...(asyncFunc
      ? { asyncFunc, extraParams }
      : options
        ? { options, searchFunc }
        : { options: [] as T[] })
  });
  // Mirrored locally because the ordering below has to run BEFORE useCombobox
  // is given its `items` — reading Downshift's own inputValue here would be
  // circular, and a mismatched order would desync highlightedIndex.
  const [searchTerm, setSearchTerm] = useState("");
  const _onInputValueChange = (changes: UseComboboxStateChange<T>) => {
    setSearchTerm(changes.inputValue || "");
    onInputValueChange(changes);
  };
  const items = useMemo(() => {
    if (!prioritizePrefixMatches || !searchTerm.trim()) return fetchedItems;
    const scope = buildLegSearchScope(searchTerm);
    const hits: T[] = [];
    const rest: T[] = [];
    fetchedItems.forEach((item) => {
      (scope.matches(_itemToString(item)) ? hits : rest).push(item);
    });
    // "sarande -" / "- sarande" name a direction, so the other one is cut; bare
    // "sarande" keeps it, below the hits. Array#sort isn't used on purpose:
    // partitioning keeps the backend's alphabetical order inside each group.
    return scope.mode === "filter" ? hits : [...hits, ...rest];
  }, [fetchedItems, searchTerm, prioritizePrefixMatches]);
  const _onSelectedItemChange = (changes: UseComboboxStateChange<T>) => {
    if (changes?.selectedItem) {
      if (selectType === "single")
        onSelectedItemChange && onSelectedItemChange(changes.selectedItem);
      else if (selectType === "multiple") {
        let _selectedItems = [...(selectedItems || [])];
        const isSelected = _selectedItems.some((el) =>
          isEqual(getOptionValue(el), getOptionValue(changes.selectedItem as T))
        );
        if (isSelected)
          _selectedItems = _selectedItems.filter(
            (el) => !isEqual(getOptionValue(el), getOptionValue(changes.selectedItem as T))
          );
        else _selectedItems.push(changes.selectedItem);
        onSelectedListChange &&
          onSelectedListChange(_selectedItems, changes.selectedItem);
        // Fire only for Enter (not mouse ItemClick) on the opt-in picker, so the
        // consumer can advance focus once. The reducer closes the menu below.
        if (
          closeOnEnter &&
          changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter
        )
          onEnterSelect?.(changes.selectedItem);
      }
    }
  };
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
    getToggleButtonProps,
    openMenu,
    setInputValue
  } = useCombobox<T>({
    defaultIsOpen,
    onInputValueChange: _onInputValueChange,
    items,
    itemToString: _itemToString,
    onSelectedItemChange: _onSelectedItemChange,
    selectedItem: selectType === "multiple" ? null : _selectedItem || null,
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          // inlineSearch: the input holds the user's search term, and Downshift
          // would overwrite it with the picked item's label. Keep the term (and
          // the menu open) so several matches can be ticked off one search.
          if (inlineMultiSearch)
            return { ...changes, isOpen: true, inputValue: state.inputValue };
          // Opt-in picker: Enter on a HIGHLIGHTED option closes the menu
          // (Downshift's default) so the consumer can move focus on. Enter with
          // nothing highlighted, and every non-opt-in multi-select, keep the
          // menu open exactly as before. Clear inputValue so reopening the
          // picker shows a fresh search box (matches multi-select's blur reset).
          if (closeOnEnter && state.highlightedIndex >= 0)
            return { ...changes, inputValue: "" };
          return {
            ...changes,
            ...(selectType === "multiple" && { isOpen: true })
          };
        case useCombobox.stateChangeTypes.ItemClick:
          if (inlineMultiSearch)
            return { ...changes, isOpen: true, inputValue: state.inputValue };
          return {
            ...changes,
            ...(selectType === "multiple" && { isOpen: true })
          };
        // Multi-select pins `selectedItem` to null (that's what makes every click
        // a null -> item change, so toggling fires). One render after a click,
        // Downshift notices its internal selectedItem no longer matches that null
        // prop and dispatches this to resync, carrying
        // inputValue: itemToString(null) — i.e. "". That lands a render AFTER
        // ItemClick, so preserving the term there isn't enough; it has to be
        // preserved here too, or picking an option wipes the search box.
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          if (inlineMultiSearch)
            return { ...changes, inputValue: state.inputValue };
          return changes;
        case useCombobox.stateChangeTypes.InputBlur:
          // inlineSearch: blanking on blur would leave the term gone but the
          // option list still filtered by it. Keep both in sync — the clear
          // button (which also refetches) is the way to reset.
          if (inlineMultiSearch)
            return { ...changes, inputValue: state.inputValue };
          return {
            ...changes,
            inputValue:
              _itemToString(changes.selectedItem as T) || changes.inputValue,
            ...(selectType === "multiple" && { inputValue: "" })
          };
        default:
          return changes;
      }
    }
  });
  return (
    <div className={cx(["relative w-full", className])}>
      <SelectInput
        selectedItems={selectedItems}
        selectType={selectType}
        onSelectedItemChange={_onSelectedItemChange}
        getInputProps={getInputProps}
        getToggleButtonProps={getToggleButtonProps}
        inputClassName={inputClassName}
        loading={items.length ? loading : false}
        isOpen={isOpen}
        getOptionLabel={getOptionLabel}
        enableKeyboardNav={keyboardNav}
        inlineSearch={inlineMultiSearch}
        openMenu={openMenu}
        onClearSearch={() => {
          setInputValue("");
          // Downshift's document-level mouseup sees the clear button as outside
          // the input/menu/toggle refs and dispatches InputBlur, so reopen after
          // wiping the term — the input never lost focus (mousedown is
          // prevented), only the menu closed.
          openMenu();
        }}
        {...props}
      />
      <List
        highlightedIndex={highlightedIndex}
        getInputProps={getInputProps}
        selectType={selectType}
        items={items}
        isOpen={isOpen}
        handleScroll={handleScroll}
        getMenuProps={getMenuProps}
        getItemProps={getItemProps}
        selectedItem={selectedItem}
        selectedItems={selectedItems}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        maxHeightList={maxHeightList}
        isAbsolute={isAbsolute}
        enableKeyboardNav={keyboardNav}
        inlineSearch={inlineMultiSearch}
        onSelectAll={onSelectAll}
        selectAllLabel={selectAllLabel}
        onUnselectAll={onUnselectAll}
        unselectAllLabel={unselectAllLabel}
      />
    </div>
  );
};
export default Select;
